import React, { useEffect, useState } from 'react';
import utmConfig from './config/utm-config.json';
import { useCookies } from 'react-cookie';

interface UtmData {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  ID: string;
  [key: string]: string;
}

const Campaign = (): any => {
  const [cookies, setCookie] = useCookies(['promoCode']);
  const [promoCodeValue, setPromoCodeValue] = useState();
  const [leadValue, setLeadValue] = useState<UtmData>();
  const [conversionValue, setConversionValue] = useState<UtmData>();

  useEffect(() => {
    const urlParams = new URLSearchParams(document.location.search);

    const getPromoCode = () => {
      try {
        // Check if there is a localStorage
        if (!localStorage) {
          return;
        }

        let promoValue = cookies.promoCode;
        if (!promoValue) {
          promoValue = urlParams.get('r');
          if (promoValue) {
            setCookie('promoCode', promoValue);
          }
        }
        setPromoCodeValue(promoValue);
        return promoValue;
      } catch (ex) {
        return;
      }
    };

    const getLead = (promoCodeValue: string) => {
      try {
        // Check if there is a localStorage
        if (!localStorage) {
          return;
        }

        const existingLeadData = localStorage.getItem('lead');

        if (!existingLeadData && promoCodeValue in utmConfig) {
          let lead = utmConfig[promoCodeValue as keyof typeof utmConfig];
          const updatedLeadValue = {
            source: lead.source,
            medium: lead.medium,
            campaign: lead.campaign,
            term: lead.term,
            content: lead.content,
            ID: lead.ID,
          };
          setLeadValue(updatedLeadValue);
          localStorage.setItem('lead', JSON.stringify(updatedLeadValue));
          return updatedLeadValue;
        }

        if (existingLeadData) {
          const parsedLeadData: UtmData = JSON.parse(existingLeadData);
          localStorage.setItem('lead', JSON.stringify(parsedLeadData));
          setLeadValue(parsedLeadData);
        } else {
          const newLeadValue = {
            source: urlParams.get('utm_source') || ' ',
            medium: urlParams.get('utm_medium') || 'web',
            campaign: urlParams.get('utm_campaign') || 'website',
            term: urlParams.get('utm_term') || ' ',
            content: urlParams.get('utm_content') || ' ',
            ID: urlParams.get('utm_ID') || urlParams.get('utm_id') || ' ',
          };

          setLeadValue(newLeadValue);

          localStorage.setItem('lead', JSON.stringify(newLeadValue));
        }

        for (const key in leadValue) {
          if (!leadValue[key]) {
            localStorage.removeItem(`utm_${key}`);
          }
        }

        return leadValue;
      } catch (error) {
        console.error('Error processing lead data:', error);
      }
    };

    const getConversion = (promoCodeValue: string) => {
      try {
        // Check if there is a localStorage
        if (!localStorage) {
          return;
        }

        if (window.location.href.indexOf(`?r=`) !== -1 && promoCodeValue in utmConfig) {
          let conversion = utmConfig[promoCodeValue as keyof typeof utmConfig];
          localStorage.setItem('conversion', JSON.stringify(conversion));
          setConversionValue((prevConversionValue) => ({
            ...prevConversionValue,
            source: conversion.source,
            medium: conversion.medium,
            campaign: conversion.campaign,
            term: conversion.term,
            content: conversion.content,
            ID: conversion.ID,
          }));

          return conversionValue;
        }

        setConversionValue((prevConversionValue) => ({
          ...prevConversionValue,
          source: urlParams.get('utm_source') || ' ',
          medium: urlParams.get('utm_medium') || ' ',
          campaign: urlParams.get('utm_campaign') || ' ',
          term: urlParams.get('utm_term') || ' ',
          content: urlParams.get('utm_content') || ' ',
          ID: urlParams.get('utm_ID') || urlParams.get('utm_id') || ' ',
        }));

        if (!urlParams.get('utm_source')) return;

        for (const key in conversionValue) {
          if (!conversionValue[key]) {
            localStorage.removeItem(`utm_${key}`);
          }
        }

        localStorage.setItem('conversion', JSON.stringify(conversionValue));
        return conversionValue;
      } catch (error) {
        console.error('Error processing conversion data:', error);
      }
    };

    const setPartnerMedium = () => {
      if (window.location.href.includes('partner')) {
        setLeadValue((prevLeadValue) => {
          if (!prevLeadValue) return prevLeadValue;
          const updatedLeadValue = {
            ...prevLeadValue,
            medium: 'Partner Landing Page',
          };
          localStorage.setItem('lead', JSON.stringify(updatedLeadValue));
          return updatedLeadValue;
        });
      }
    };

    const setMvfMedium = () => {
      if (window.location.href.includes('mvf')) {
        setLeadValue((prevLeadValue) => {
          if (!prevLeadValue) return prevLeadValue;
          const currentURL = window.location.href;
          const mvfSource = currentURL.split('/signup-')[1].replace('/', '');
          const updatedLeadValue = {
            ...prevLeadValue,
            source: mvfSource,
            medium: 'MVF',
          };
          localStorage.setItem('lead', JSON.stringify(updatedLeadValue));
          return updatedLeadValue;
        });
      }
    };

    getPromoCode();
    getLead(promoCodeValue);
    getConversion(promoCodeValue);
    setPartnerMedium();
    setMvfMedium();
  }, [promoCodeValue, setLeadValue, setConversionValue]);

  return null;
};

export default Campaign;
